import { useState } from 'react';
import { Link } from 'react-router-dom';
import { downloadFile } from '@/lib/downloadFile';
import type { NostrEvent } from '@nostrify/nostrify';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, CalendarDays, Download, ExternalLink, Hash, Loader2, MoreVertical, Pencil, Trash2, Copy } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { KIND_MAGAZINE_ISSUE } from '@/lib/pressstrKinds';
import { EditIssueDialog } from './EditIssueDialog';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { useToast } from '@/hooks/useToast';

interface IssueCardProps {
  event: NostrEvent;
  onDeleted?: () => void;
}

export function IssueCard({ event, onDeleted }: IssueCardProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const isOwner = user?.pubkey === event.pubkey;

  const dTag = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
  const naddr = nip19.naddrEncode({ kind: KIND_MAGAZINE_ISSUE, pubkey: event.pubkey, identifier: dTag });

  const copyNaddr = async (e: React.MouseEvent) => {
    e.preventDefault();
    await navigator.clipboard.writeText(naddr).catch(() => {});
    toast({ title: 'Copied!', description: 'naddr copied to clipboard.' });
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!url || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile(url, `${title.replace(/[/\\:*?"<>|]/g, '_')}.pdf`);
    } catch {
      toast({ title: 'Download failed', description: 'Could not download the file.', variant: 'destructive' });
    } finally {
      setIsDownloading(false);
    }
  };

  const title = event.tags.find(([n]) => n === 'title')?.[1] ?? 'Untitled Issue';
  const issue = event.tags.find(([n]) => n === 'issue')?.[1];
  const image = event.tags.find(([n]) => n === 'image')?.[1];
  const url = event.tags.find(([n]) => n === 'url')?.[1];
  const summary = event.tags.find(([n]) => n === 'summary')?.[1];
  const publishedAt = event.tags.find(([n]) => n === 'published_at')?.[1];

  const publishDate = publishedAt
    ? new Date(parseInt(publishedAt) * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })
    : new Date(event.created_at * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });

  return (
    <>
      <div className="relative group">
        <Link to={`/${naddr}`}>
          <Card className="overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 h-full flex flex-col">

            {/* ── Cover ─────────────────────────────────────── */}
            <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-4 flex items-center justify-center">
              {image ? (
                /* Portrait cover image — centred, with a soft shadow */
                <img
                  src={image}
                  alt={title}
                  className="w-full max-w-[10rem] aspect-[2/3] object-cover rounded-md shadow-md group-hover:scale-[1.03] transition-transform duration-300 mx-auto block"
                />
              ) : (
                /* Placeholder when no cover */
                <div className="w-full max-w-[10rem] aspect-[2/3] rounded-md bg-slate-200 dark:bg-slate-700 flex items-center justify-center mx-auto">
                  <BookOpen className="w-10 h-10 text-slate-400" />
                </div>
              )}

              {/* PDF pill — top-left */}
              <Badge className="absolute top-2 left-2 bg-blue-600 text-white border-0 text-[10px] px-1.5 py-0.5 pointer-events-none">
                PDF
              </Badge>

              {/* Issue # pill — top-right (only when there's a cover so it doesn't look orphaned) */}
              {issue && (
                <Badge variant="secondary" className="absolute top-2 right-8 text-[10px] px-1.5 py-0.5 pointer-events-none">
                  <Hash className="w-2.5 h-2.5 mr-0.5" />{issue}
                </Badge>
              )}
            </div>

            {/* ── Info ──────────────────────────────────────── */}
            <CardContent className="p-3 flex-1 flex flex-col gap-1.5">
              {/* Date */}
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarDays className="w-3 h-3 shrink-0" />{publishDate}
              </span>

              {/* Title */}
              <h3 className="font-semibold text-sm leading-snug line-clamp-2 break-words group-hover:text-primary transition-colors">
                {title}
              </h3>

              {/* Summary */}
              {summary && (
                <p className="text-xs text-muted-foreground line-clamp-2 break-words">{summary}</p>
              )}

              {/* Quick-action row */}
              {url && (
                <div
                  className="flex gap-1.5 mt-auto pt-1"
                  onClick={(e) => e.preventDefault()}
                >
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Read online"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />Read
                  </a>
                  <button
                    disabled={isDownloading}
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                    title="Download"
                  >
                    {isDownloading
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</>
                      : <><Download className="w-3.5 h-3.5" />Save</>
                    }
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        {/* Overflow menu — appears on hover, top-right corner */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                onClick={(e) => e.preventDefault()}
              >
                <MoreVertical className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={copyNaddr}>
                <Copy className="w-4 h-4 mr-2" />Copy naddr
              </DropdownMenuItem>
              {isOwner && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); setEditOpen(true); }}>
                    <Pencil className="w-4 h-4 mr-2" />Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.preventDefault(); setDeleteOpen(true); }} className="text-destructive focus:text-destructive">
                    <Trash2 className="w-4 h-4 mr-2" />Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <EditIssueDialog open={editOpen} onOpenChange={setEditOpen} issue={event} onSuccess={onDeleted} />
      <ConfirmDeleteDialog open={deleteOpen} onOpenChange={setDeleteOpen} eventId={event.id} label="Issue" onSuccess={onDeleted} />
    </>
  );
}
